import { useState } from "react";
import { motion } from "framer-motion";
import { MENTORS } from "../../../data/home-data";

// Two alternating rows for the marquee.
const rowA = MENTORS.filter((_, i) => i % 2 === 0);
const rowB = MENTORS.filter((_, i) => i % 2 === 1);

// These brands' logos read small, so render them larger; everything else smaller.
const BIG_LOGOS = new Set(["Amazon", "Oracle", "Salesforce", "Rippling"]);

// Company logo — falls back to the company name only if the image is missing
// (so there's no logo + name duplication).
function CompanyLogo({ m }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="text-sm font-semibold text-gray-800">{m.company}</span>
    );
  }
  const sizeCls = BIG_LOGOS.has(m.company)
    ? "h-12 max-w-[150px]"
    : "h-7 max-w-[110px]";
  return (
    <img
      src={m.logo}
      alt={m.company}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-auto object-contain ${sizeCls}`}
    />
  );
}

function MentorCard({ m }) {
  return (
    <div className="shrink-0 w-60 rounded-3xl bg-white ring-1 ring-gray-100 shadow-sm hover:shadow-xl transition overflow-hidden">
      {/* Photo */}
      <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
        <img
          src={m.img}
          alt={m.name}
          loading="lazy"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />

        {/* domain pill */}
        {/* <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-white/90 text-aiblue shadow-sm">
          {m.domain}
        </span> */}
        {/* name + role over photo */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <p className="font-display font-bold leading-tight">{m.name}</p>
          <p className="text-xs text-white/85 leading-snug mt-0.5">{m.role}</p>
        </div>
      </div>

      {/* company logo only (no duplicate name) */}
      <div className="flex items-center px-4 py-3 h-[4.5rem]">
        <CompanyLogo m={m} />
      </div>
    </div>
  );
}

function MentorMarquee({ items, reverse = false }) {
  return (
    <div className="group relative overflow-hidden py-2">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-white to-transparent z-10" />
      <div
        className={`flex gap-5 w-max hp-marquee ${
          reverse ? "hp-marquee-reverse" : ""
        }`}
      >
        {[...items, ...items].map((m, i) => (
          <MentorCard key={`${m.name}-${i}`} m={m} />
        ))}
      </div>
    </div>
  );
}

export default function SmruMentors() {
  return (
    <section
      id="mentors"
      className="pt-10 sm:pt-14 pb-20 sm:pb-28 bg-gradient-to-b from-white via-aisky/10 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-aiorange/10 text-aiorange">
            The Elite Mentorship Network
          </span>
          <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900">
            Mentored by engineers from the{" "}
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              world's top tech companies
            </span>
          </h2>
          <p className="mt-4 text-gray-600 text-base sm:text-lg">
            Don't just learn to code learn how global tech giants actually build,
            scale, and deploy. Get direct, real-world insights and career
            blueprints from industry leaders whose products are used by millions
            across the globe daily.
          </p>
        </motion.div>

        {/* Two-line marquee — constrained within the container, like the companies row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-14 space-y-5"
        >
          <MentorMarquee items={rowA} />
          <MentorMarquee items={rowB} reverse />
        </motion.div>
      </div>
    </section>
  );
}
